import { X } from "lucide-react";

import Poll2FeedbackForm from "./Poll2FeedbackForm";
import {
  formatSponsorLabel,
  poll2FilledCount,
  type Poll2Dimension,
  type Poll2Draft,
  type Poll2Tier,
} from "./poll2Helpers";
import CommonModal from "../../Components/Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
  sponsorName?: string;
  sponsorRole?: "primary" | "secondary" | string | null;
  cycleName?: string;
  period: string;
  accountId: string;
  dimensions: Poll2Dimension[];
  poll2Tiers: Poll2Tier[];
  draft: Poll2Draft;
  submitted: boolean;
  submitting: boolean;
  savingDraft?: boolean;
  onDraftChange: (draft: Poll2Draft) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
};

const Poll2CheckInModal = ({
  isOpen,
  onClose,
  accountName,
  sponsorName,
  sponsorRole,
  cycleName,
  period,
  accountId,
  dimensions,
  poll2Tiers,
  draft,
  submitted,
  submitting,
  savingDraft,
  onDraftChange,
  onSaveDraft,
  onSubmit,
}: Props) => {
  const filled = poll2FilledCount(draft);
  const total = dimensions.length || 3;
  const progressPercent = total > 0 ? Math.round((filled / total) * 100) : 0;

  const chips = Array.from(
    new Set(
      [period, formatSponsorLabel(sponsorName, sponsorRole)].filter(
        Boolean
      ) as string[]
    )
  );

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      size="5xl"
      showHeader={false}
      contentClassName="overflow-hidden flex flex-col"
      usePortal
    >
      {/* ── Modal Header ── */}
      <header className="flex-shrink-0 flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-gray-900 leading-tight truncate">
            {cycleName ? `${cycleName} (${accountName})` : accountName}
          </h2>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.map(chip => (
              <span
                key={chip}
                className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-3 flex-shrink-0">
          <div className="rounded-lg bg-gray-100 border border-gray-200 px-3 pt-2 pb-3 text-center min-w-[190px]">
            <div className="text-sm font-semibold text-gray-800">
              {filled} of {total} answered
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-gray-300">
                <div
                  className="h-full rounded-full bg-purple-600 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-[10px] font-semibold text-gray-500 shrink-0">
                {progressPercent}%
              </span>
            </div>
          </div>
          <button
            type="button"
            className="mt-0.5 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </header>

      {/* ── Scrollable Body ── */}
      <section className="flex-1 min-h-0 overflow-y-auto mt-4">
        <Poll2FeedbackForm
          accountId={accountId}
          accountName={accountName}
          sponsorName={sponsorName}
          sponsorRole={sponsorRole}
          dimensions={dimensions}
          poll2Tiers={poll2Tiers}
          draft={draft}
          submitted={submitted}
          submitting={submitting}
          savingDraft={savingDraft}
          showHeader={false}
          onDraftChange={onDraftChange}
          onSaveDraft={onSaveDraft}
          onSubmit={onSubmit}
        />
      </section>
    </CommonModal>
  );
};

export default Poll2CheckInModal;
